import { Button, Checkbox, Form, Input, InputNumber, Select, Space, message } from "antd";
import { useEffect, useMemo, useState } from "react";
import { CopyOutlined } from "@ant-design/icons";
import { serverFetch } from "@core/server";
import { CHANNEL_VID_SITE, dictsChannelVids } from "../channel-vids";

const errMsg = (e) => e?.message || "Ошибка";

const copyToClipboard = (text, label = "Текст") => {
  if (!text) return;
  navigator.clipboard.writeText(text).then(
    () => message.success(`${label} скопирован`),
    () => message.error("Не удалось скопировать")
  );
};

const defaultItem = { active: true, name: "", code: "", vid: 1, idx: 0, providerId: null, agentId: null };

const ChannelForm = ({ id, onClose }) => {
  const [item, setItem] = useState(defaultItem);
  const [loading, setLoading] = useState(false);
  const [providers, setProviders] = useState([]);
  const [agents, setAgents] = useState([]);
  const [form] = Form.useForm();

  useEffect(() => {
    serverFetch("Message/Provider")
      .then((data) => setProviders(Array.isArray(data) ? data : []))
      .catch(() => {});
    serverFetch("Agent")
      .then((data) => setAgents(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const providerOptions = useMemo(() => {
    const vid = item.vid ?? 1;
    const filtered = providers.filter((p) => p.vid === vid);
    const opts = filtered.map((p) => ({ value: p.id, label: p.name }));
    return [{ value: null, label: "Не указан" }, ...opts];
  }, [providers, item.vid]);

  const selectedProvider = useMemo(
    () => (item.providerId != null ? providers.find((p) => p.id === item.providerId) : null),
    [providers, item.providerId]
  );

  const agentOptions = useMemo(() => {
    const opts = agents
      .filter((a) => a.active || a.id === item.agentId)
      .map((a) => ({ value: a.id, label: a.active ? a.name : `${a.name} (неактивен)` }));
    return [{ value: null, label: "Не указан" }, ...opts];
  }, [agents, item.agentId]);

  useEffect(() => {
    if (id) {
      setLoading(true);
      serverFetch(`Message/Channel/${id}`)
        .then((data) => {
          setItem(data);
          form.setFieldsValue(data);
        })
        .catch((e) => message.error(errMsg(e)))
        .finally(() => setLoading(false));
    } else {
      form.setFieldsValue(defaultItem);
      setItem(defaultItem);
    }
  }, [id, form]);

  const onValuesChange = (value) => {
    if ("vid" in value) {
      const newVid = value.vid ?? item.vid;
      const allowedIds = providers.filter((p) => p.vid === newVid).map((p) => p.id);
      if (item.providerId != null && !allowedIds.includes(item.providerId)) {
        form.setFieldsValue({ providerId: null });
        setItem({ ...item, ...value, providerId: null });
        return;
      }
    }
    setItem({ ...item, ...value });
  };

  const onSave = () => {
    form.validateFields().then(() => {
      setLoading(true);
      const payload = {
        ...item,
        name: item.name ?? "",
        code: item.code ?? "",
        vid: item.vid ?? 1,
        idx: item.idx ?? 0,
        active: item.active ?? true,
        providerId: item.providerId ?? null,
        agentId: item.agentId ?? null
      };
      if (id) payload.id = id;
      serverFetch("Message/Channel", { method: id ? "PUT" : "POST", bodyData: payload })
        .then(() => onClose(true))
        .catch((e) => {
          setLoading(false);
          message.error(errMsg(e));
        });
    });
  };

  return (
    <Form
      form={form}
      onValuesChange={onValuesChange}
      labelCol={{ span: 7 }}
      wrapperCol={{ span: 17 }}
      colon={false}
      disabled={loading}
      className="msg-channel-form"
    >
      <Form.Item name="active" valuePropName="checked" label=" ">
        <Checkbox>Активный</Checkbox>
      </Form.Item>
      <Form.Item name="name" label="Наименование" rules={[{ required: true, message: "Введите наименование" }]}>
        <Input placeholder="Наименование канала" />
      </Form.Item>
      {Number(item.vid) === CHANNEL_VID_SITE && item.code != null && item.code !== "" && (
        <>
          <Form.Item label="Код">
            <Space.Compact style={{ width: "100%" }}>
              <Input value={item.code} readOnly />
              <Button type="default" icon={<CopyOutlined />} onClick={() => copyToClipboard(item.code, "Код")} />
            </Space.Compact>
          </Form.Item>
          <Form.Item label="Script">
            <Space direction="vertical" style={{ width: "100%" }}>
              <Input.TextArea value={item.script} readOnly rows={6} style={{ fontFamily: "monospace" }} />
              <Button type="default" icon={<CopyOutlined />} onClick={() => copyToClipboard(item.script, "Script")}>
                Копировать
              </Button>
            </Space>
          </Form.Item>
        </>
      )}
      {Number(item.vid) !== CHANNEL_VID_SITE && (
        <Form.Item label="Код">
          <Space.Compact style={{ width: "100%" }}>
            <Form.Item name="code" noStyle>
              <Input placeholder="API ключ / токен" />
            </Form.Item>
            {item.code ? (
              <Button type="default" icon={<CopyOutlined />} onClick={() => copyToClipboard(item.code, "Код")} />
            ) : null}
          </Space.Compact>
        </Form.Item>
      )}
      <Form.Item name="vid" label="Вид канала" rules={[{ required: true }]}>
        <Select options={dictsChannelVids} disabled={!!id} />
      </Form.Item>
      <Form.Item name="idx" label="Порядок">
        <InputNumber min={0} style={{ width: "100%" }} />
      </Form.Item>
      <Form.Item name="agentId" label="Агент">
        <Select placeholder="Не указан" allowClear options={agentOptions} />
      </Form.Item>
      {providerOptions.length > 1 && (
        <>
          <Form.Item name="providerId" label="Провайдер">
            <Select placeholder="Не указан" allowClear options={providerOptions} />
          </Form.Item>
          {selectedProvider?.url ? (
            <Form.Item label=" ">
              <a href={selectedProvider.url} target="_blank" rel="noopener noreferrer">
                Регистрация
              </a>
            </Form.Item>
          ) : null}
          {selectedProvider?.description ? (
            <Form.Item label="Описание">
              <Input.TextArea value={selectedProvider.description} readOnly rows={3} />
            </Form.Item>
          ) : null}
        </>
      )}
      {id && item.createdOn ? (
        <Form.Item label="Дата создания">
          <Input value={new Date(item.createdOn).toLocaleString("ru-RU")} disabled />
        </Form.Item>
      ) : null}
      <Form.Item label=" ">
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Button type="primary" onClick={onSave} loading={loading}>
            Сохранить
          </Button>
          <Button onClick={() => onClose(false)}>Закрыть</Button>
        </Space>
      </Form.Item>
    </Form>
  );
};

export default ChannelForm;
